import {
  AuditIllustration,
  MediaBuyingIllustration,
  FunnelIllustration,
} from "../ProcessIllustrations";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const steps = [
  {
    num: "01",
    title: "We learn your business",
    body: "The first week, we get to know how you make money, who your best customers are, and what you've tried before. If you're already running ads, we audit them. If you're not, we map out where to start. Either way, you get a straight plan ranked by what will bring in customers fastest.",
    Illustration: AuditIllustration,
  },
  {
    num: "02",
    title: "We build a system to scale",
    body: "From day one we set up a scaling framework, not just a handful of ads. We work in phases: prove what converts first, then put budget behind the winners and grow from there. Each phase has a clear target, so scaling up is a decision backed by numbers, never a guess. Both of us stay hands-on the whole way.",
    Illustration: MediaBuyingIllustration,
  },
  {
    num: "03",
    title: "Conversion rate optimization",
    body: "We optimize every touchpoint in your funnel, not just one page. Landing pages, ad creative, booking flows, follow-up emails, all of it. A few percentage points better at each step compounds fast, and those small gains across the funnel add up to a real drop in what it costs you to land a customer.",
    Illustration: FunnelIllustration,
  },
];

function Step({ step, index }) {
  const isEven = index % 2 === 0;
  const { Illustration } = step;
  const [ref, vis] = useScrollReveal(0.1);

  return (
    <div
      ref={ref}
      data-testid={`bend-process-step-${step.num}`}
      className="relative"
    >
      {/* Watermark number */}
      <div className={`watermark ${isEven ? 'left-0 md:-left-8' : 'right-0 md:-right-8'} top-0`}>
        {step.num}
      </div>

      <div className={`relative z-10 grid md:grid-cols-2 gap-12 md:gap-20 lg:gap-28 items-center ${
        isEven ? '' : 'md:[direction:rtl]'
      }`}>
        {/* Text */}
        <div className={`${isEven ? 'reveal-left' : 'reveal-right'} ${vis ? 'visible' : ''} ${!isEven ? 'md:[direction:ltr]' : ''}`}>
          <span className="step-num font-['Playfair_Display'] font-semibold text-[20px] md:text-[22px] block mb-3">
            Step {step.num}
          </span>
          <h3 className="font-['Playfair_Display'] font-semibold text-[28px] md:text-[34px] lg:text-[40px] text-[#0F172A] leading-[1.1] tracking-[-0.02em]">
            {step.title}
          </h3>
          <p className="mt-5 text-[16px] md:text-[17px] leading-[1.7] text-[#475569] max-w-[480px]">
            {step.body}
          </p>
        </div>

        {/* Illustration */}
        <div className={`illus-glow ${isEven ? 'reveal-right' : 'reveal-left'} ${vis ? 'visible' : ''} ${!isEven ? 'md:[direction:ltr]' : ''}`}>
          <Illustration />
        </div>
      </div>
    </div>
  );
}

export const BendProcess = () => {
  const [headerRef, headerVis] = useScrollReveal();

  return (
    <section
      id="process"
      data-testid="bend-process-section"
      className="bg-white py-28 md:py-40 lg:py-48 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div ref={headerRef} className={`text-center max-w-[600px] mx-auto mb-24 md:mb-36 reveal ${headerVis ? 'visible' : ''}`}>
          <h2
            data-testid="bend-process-heading"
            className="font-['Playfair_Display'] font-semibold text-[clamp(24px,3vw,40px)] tracking-[-0.03em] text-[#0F172A] leading-[1.0] whitespace-nowrap"
          >
            What the first month looks like
          </h2>
        </div>

        {/* Steps */}
        <div className="space-y-28 md:space-y-40 lg:space-y-52">
          {steps.map((step, i) => (
            <Step key={step.num} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
